import React, { useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  AppBar, Toolbar, Typography, Button, IconButton,
  Box, Drawer, List, ListItem, ListItemButton, ListItemText, Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Home from './pages/Home';
import Leistungen from './pages/Leistungen';
import Praxis from './pages/Praxis';
import ÜberMich from './pages/ÜberMich';
import Kontakt from './pages/Kontakt';
import './index.css';
import logo from './assets/Logo.jpg';
import Footer from './pages/Footer';
import Impressum from './pages/Impressum';
import Datenschutzerklaerung from './pages/Datenschutzerklaerung';

const theme = createTheme({
  palette: {
    primary: { main: '#2E7D32' },
    secondary: { main: '#D4A843' },
    background: { default: '#F7F3EC', paper: '#FDFAF4' },
    text: { primary: '#2C2A27', secondary: '#6B6459' },
  },
  typography: {
    fontFamily: '"Lato", "Helvetica", "Arial", sans-serif',
    h1: { fontFamily: '"Playfair Display", serif', fontWeight: 700 },
    h2: { fontFamily: '"Playfair Display", serif', fontWeight: 700 },
    h3: { fontFamily: '"Playfair Display", serif', fontWeight: 700 },
    h4: { fontFamily: '"Playfair Display", serif', fontWeight: 600 },
    h5: { fontFamily: '"Playfair Display", serif', fontWeight: 600 },
    h6: { fontFamily: '"Playfair Display", serif', fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 700, letterSpacing: 0.3 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#FDFAF4',
          borderRadius: '20px',
          boxShadow: '0 4px 28px rgba(46, 125, 50, 0.09)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: '30px', fontWeight: 700 },
        containedPrimary: {
          background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
          boxShadow: '0 4px 18px rgba(46, 125, 50, 0.35)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1B5E20 0%, #388E3C 100%)',
            boxShadow: '0 6px 22px rgba(46, 125, 50, 0.45)',
          },
        },
      },
    },
  },
});

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'Leistungen', to: '/leistungen' },
  { label: 'Praxis', to: '/praxis' },
  { label: 'Über mich', to: '/über-mich' },
  { label: 'Kontakt', to: '/kontakt' },
];

function NavBar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Box sx={{ maxWidth: '1200px', mx: 'auto', width: '100%' }}>
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 3 }, py: 0.5 }}>

          {/* Logo + Name */}
          <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none' }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.4)',
                objectFit: 'cover',
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 600,
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                  color: '#fff',
                  lineHeight: 1.2,
                }}
              >
                Heilpraktikerin
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontStyle: 'italic',
                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                  color: 'rgba(255,255,255,0.85)',
                  lineHeight: 1.2,
                }}
              >
                Sylwia Spiller
              </Typography>
            </Box>
          </Box>

          {/* Desktop Nav */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
            {navLinks.map(({ label, to }) => (
              <Button
                key={to}
                component={Link}
                to={to}
                sx={{
                  color: decodeURIComponent(location.pathname) === to ? '#D4A843' : 'rgba(255,255,255,0.9)',
                  fontSize: '0.95rem',
                  fontWeight: decodeURIComponent(location.pathname) === to ? 700 : 400,
                  px: 2,
                  borderRadius: '8px',
                  '&:hover': {
                    color: '#fff',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                  borderBottom: decodeURIComponent(location.pathname) === to ? '2px solid #D4A843' : '2px solid transparent',
                  transition: 'all 0.2s ease',
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          {/* Mobile Hamburger */}
          <IconButton
            color="inherit"
            onClick={() => setDrawerOpen(true)}
            sx={{ display: { xs: 'flex', md: 'none' } }}
            aria-label="Menü öffnen"
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 260,
            background: 'linear-gradient(160deg, #1B5E20 0%, #2E7D32 100%)',
            color: '#fff',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: '"Playfair Display", serif', fontStyle: 'italic', color: 'rgba(255,255,255,0.9)', fontSize: '1.1rem' }}>
            Sylwia Spiller
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
        <List sx={{ pt: 1 }}>
          {navLinks.map(({ label, to }) => (
            <ListItem key={to} disablePadding>
              <ListItemButton
                component={Link}
                to={to}
                onClick={() => setDrawerOpen(false)}
                sx={{
                  py: 1.5,
                  px: 3,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontFamily: '"Lato", sans-serif',
                    fontSize: '1.05rem',
                    color: decodeURIComponent(location.pathname) === to ? '#D4A843' : 'rgba(255,255,255,0.9)',
                    fontWeight: decodeURIComponent(location.pathname) === to ? 700 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </AppBar>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', overflowX: 'hidden', backgroundColor: '#F7F3EC' }}>
        <NavBar />

        <Box sx={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/leistungen" element={<Leistungen />} />
            <Route path="/praxis" element={<Praxis />} />
            <Route path="/über-mich" element={<ÜberMich />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutzerklarung" element={<Datenschutzerklaerung />} />
          </Routes>
        </Box>

        <Footer />
      </Box>
    </ThemeProvider>
  );
}

export default App;
